import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { authApi } from '@/api/auth.api';

import { clearAuthStorage } from '@/api/client';
import { clearLastAllowedPath, seedLastAllowedPath } from '@/utils/routeAccess';
import { getPostLoginPath } from '@/utils/moduleScope';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { enabledHeadModuleCodes, getModuleEntryByLoginPath } from '@/config/moduleEntries';

import { getOrCreateDeviceId } from '@/core/auth/deviceId';
import { clearWorkspaceScopeStorage } from '@/utils/workspaceScope';

import { STORAGE_KEYS } from '@/constants/storage';

import type { ErpModule } from '@/api/platform.api';
import { isOrgOwner } from '@/utils/moduleAccess';

import type { AddApplicationRequest, OrganizationRegisterRequest } from '@/api/auth.api';
import type { AuthUser, LoginRequest, LoginResponse } from '@/types/auth';

import {
  clearEntryModuleStorage,
  persistEntryModule,
  persistWorkspaceModule,
  markWorkspaceModuleUsed,
  readStoredEntryModule,
  readStoredWorkspaceModule,
  resolveDefaultWorkspaceModule,
  clearUsedWorkspaceModules,
} from '@/utils/moduleScope';
import { platformApi } from '@/api/platform.api';
import { bootstrapSession, clearSession, refreshSessionInit, sessionToLoginResponse } from '@/store/sessionSlice';
import { setSelectedCompany, setSelectedStore } from '@/store/appSlice';

interface AuthState {
  user: AuthUser | null;
  enabledFeatures: string[];
  enabledModules: string[];
  accessibleModules: string[];
  entryModule: ErpModule | null;
  workspaceModule: ErpModule | null;
  mfaEnabled: boolean;
  isAuthenticated: boolean;
  loading: boolean;
  initialized: boolean;
  forcePasswordChange: boolean;
}

function loginToUser(data: LoginResponse): AuthUser {
  return {
    userId: data.userId,
    userUuid: data.userUuid,
    email: data.email,
    firstName: data.firstName,
    lastName: data.lastName,
    organizationId: data.organizationId,
    organizationName: data.organizationName ?? null,
    masterAdmin: data.masterAdmin,
    permissions: data.permissions ?? [],
    roleCode: data.roleCode ?? null,
    roleName: data.roleName ?? null,
    storeId: data.storeId ?? null,
  };
}

function resolveEntryModule(data: LoginResponse): ErpModule | null {
  if (data.entryModule) return data.entryModule as ErpModule;
  return null;
}

const initialState: AuthState = {
  user: null,
  enabledFeatures: [],
  enabledModules: [],
  accessibleModules: [],
  entryModule: readStoredEntryModule(),
  workspaceModule: readStoredWorkspaceModule(),
  mfaEnabled: false,
  isAuthenticated: false,
  loading: false,
  initialized: false,
  forcePasswordChange: false,
};

type ApplyLoginOptions = {
  /** True when rehydrating an existing session (page refresh), not a fresh sign-in. */
  restore?: boolean;
};

function isEnabledWorkspaceModule(module: ErpModule | null, enabledModules: string[]): boolean {
  if (!module) return false;
  return enabledHeadModuleCodes(enabledModules).includes(module);
}

function resolveLoginPathModule(): ErpModule | null {
  if (typeof window === 'undefined') return null;
  return getModuleEntryByLoginPath(window.location.pathname)?.erpModule ?? null;
}

function resolveWorkspaceModule(
  owner: boolean,
  resolvedEntry: ErpModule | null,
  enabledModules: string[],
  restore: boolean,
): ErpModule | null {
  const pendingRaw = sessionStorage.getItem(STORAGE_KEYS.PENDING_ENTRY_MODULE);
  const pendingModule = pendingRaw ? (pendingRaw as ErpModule) : null;
  const loginPathModule = resolveLoginPathModule();

  if (owner) {
    if (restore) {
      const fromPath =
        typeof window !== 'undefined' ? resolveModuleFromPath(window.location.pathname) : null;
      const stored = readStoredWorkspaceModule();
      for (const candidate of [loginPathModule, fromPath, pendingModule, stored, resolvedEntry]) {
        if (isEnabledWorkspaceModule(candidate, enabledModules)) {
          return candidate;
        }
      }
      return resolveDefaultWorkspaceModule(enabledModules);
    }
    return (loginPathModule ?? pendingModule ?? resolvedEntry) ?? resolveDefaultWorkspaceModule(enabledModules);
  }

  return (loginPathModule ?? pendingModule ?? resolvedEntry) ?? resolveDefaultWorkspaceModule(enabledModules);
}

function applySessionModuleLists(state: AuthState, data: LoginResponse) {
  state.enabledFeatures = data.enabledFeatures ?? [];
  state.enabledModules = enabledHeadModuleCodes(data.enabledModules ?? []);
  state.accessibleModules = data.accessibleModules ?? [];
}

function patchUserFromSessionRefresh(state: AuthState, data: LoginResponse) {
  if (!state.user) return;
  state.user.roleCode = data.roleCode ?? state.user.roleCode;
  state.user.roleName = data.roleName ?? state.user.roleName;
  state.user.permissions = data.permissions ?? state.user.permissions;
  state.user.firstName = data.firstName ?? state.user.firstName;
  state.user.lastName = data.lastName ?? state.user.lastName;
  state.user.organizationName = data.organizationName ?? state.user.organizationName;
}

function applyLoginPayload(state: AuthState, data: LoginResponse, options?: ApplyLoginOptions) {
  const restore = options?.restore === true;
  state.user = loginToUser(data);
  state.isAuthenticated = true;
  applySessionModuleLists(state, data);
  state.mfaEnabled = data.mfaEnabled === true;
  state.forcePasswordChange = data.forcePasswordChange ?? false;

  if (data.masterAdmin) {
    state.entryModule = null;
    state.workspaceModule = null;
    clearEntryModuleStorage();
    sessionStorage.removeItem(STORAGE_KEYS.PENDING_ENTRY_MODULE);
    if (!restore) {
      seedLastAllowedPath('/master');
    }
    return;
  }

  const owner = state.user && isOrgOwner(state.user.permissions, state.user.roleCode);
  const resolvedEntry = resolveEntryModule(data);
  state.entryModule = owner ? null : resolvedEntry;
  state.workspaceModule = resolveWorkspaceModule(owner, resolvedEntry, state.enabledModules, restore);
  persistEntryModule(state.entryModule);
  persistWorkspaceModule(state.workspaceModule);
  markWorkspaceModuleUsed(state.workspaceModule ?? state.entryModule);
  sessionStorage.removeItem(STORAGE_KEYS.PENDING_ENTRY_MODULE);
  if (!restore) {
    const landing = getPostLoginPath(state.workspaceModule ?? state.entryModule, '/org', {
      permissions: state.user?.permissions ?? [],
      accessibleModules: state.accessibleModules,
      roleCode: state.user?.roleCode,
    });
    seedLastAllowedPath(landing);
  }
}

export const restoreSession = bootstrapSession;

function kickoffSessionBootstrap(dispatch: (action: ReturnType<typeof refreshSessionInit>) => unknown) {
  void dispatch(refreshSessionInit(undefined));
}

function finishAuthSession(
  dispatch: (action: PayloadAction<LoginResponse> | ReturnType<typeof refreshSessionInit>) => unknown,
  data: LoginResponse,
) {
  dispatch({ type: 'auth/hydrateFromLogin', payload: data });
  kickoffSessionBootstrap(dispatch);
}

async function clearPriorSessionBeforeSignup() {
  clearWorkspaceScopeStorage();
  try {
    await authApi.logout();
  } catch {
    /* no active session */
  }
}

export const registerAccount = createAsyncThunk(
  'auth/register',
  async (body: OrganizationRegisterRequest, { dispatch }) => {
    void clearPriorSessionBeforeSignup();
    dispatch(setSelectedCompany(null));
    dispatch(setSelectedStore(null));
    const data = await authApi.register(body);
    finishAuthSession(dispatch, data);
    return data;
  },
);

export const addOrgApplication = createAsyncThunk(
  'auth/addApplication',
  async (body: AddApplicationRequest, { dispatch }) => {
    clearWorkspaceScopeStorage();
    dispatch(setSelectedCompany(null));
    dispatch(setSelectedStore(null));
    const data = await authApi.addApplication(body);
    finishAuthSession(dispatch, data);
    return data;
  },
);

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: Omit<LoginRequest, 'deviceId'>, { dispatch }) => {
    clearWorkspaceScopeStorage();
    dispatch(setSelectedCompany(null));
    dispatch(setSelectedStore(null));
    const data = await authApi.login({
      ...credentials,
      deviceId: getOrCreateDeviceId(),
    });
    if (!data.mfaRequired) {
      finishAuthSession(dispatch, data);
    }
    return data;
  },
);

export const verifyMfaLogin = createAsyncThunk(
  'auth/verifyMfa',
  async (payload: { mfaToken: string; totpCode: string; entryModule?: string; masterLogin?: boolean }, { dispatch }) => {
    const data = await authApi.verifyMfa({
      mfaToken: payload.mfaToken,
      totpCode: payload.totpCode,
      deviceId: getOrCreateDeviceId(),
      entryModule: payload.entryModule,
      masterLogin: payload.masterLogin,
    });
    dispatch(setSelectedCompany(null));
    dispatch(setSelectedStore(null));
    finishAuthSession(dispatch, data);
    return data;
  },
);

export const completeSsoLogin = createAsyncThunk(
  'auth/ssoExchange',
  async (payload: { exchangeToken: string; deviceId?: string; entryModule?: string }, { dispatch }) => {
    void clearPriorSessionBeforeSignup();
    dispatch(setSelectedCompany(null));
    dispatch(setSelectedStore(null));
    const data = await authApi.ssoExchange({
      exchangeToken: payload.exchangeToken,
      deviceId: payload.deviceId ?? getOrCreateDeviceId(),
      entryModule: payload.entryModule,
    });
    if (!data.mfaRequired) {
      finishAuthSession(dispatch, data);
    }
    return data;
  },
);

export const switchWorkspace = createAsyncThunk(
  'auth/switchWorkspace',
  async (module: ErpModule, { dispatch }) => {
    const data = await platformApi.switchWorkspace(module, getOrCreateDeviceId());
    finishAuthSession(dispatch, data);
    return { data, module };
  },
);

export const logout = createAsyncThunk('auth/logout', async (_, { dispatch }) => {
  try {
    await authApi.logout();
  } finally {
    clearAuthStorage();
    clearEntryModuleStorage();
    clearUsedWorkspaceModules();
    clearLastAllowedPath();
    dispatch(setSelectedCompany(null));
    dispatch(setSelectedStore(null));
    dispatch(clearSession());
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setInitialized: (state) => {
      state.initialized = true;
    },
    hydrateFromLogin: (state, action: PayloadAction<LoginResponse>) => {
      applyLoginPayload(state, action.payload);
    },
    clearAuth: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.enabledFeatures = [];
      state.enabledModules = [];
      state.accessibleModules = [];
      state.entryModule = null;
      state.workspaceModule = null;
      state.mfaEnabled = false;
      state.forcePasswordChange = false;
      clearAuthStorage();
      clearEntryModuleStorage();
      clearLastAllowedPath();
    },
    setEnabledModules: (state, action: PayloadAction<string[]>) => {
      state.enabledModules = action.payload;
    },
    setAccessibleModules: (state, action: PayloadAction<string[]>) => {
      state.accessibleModules = action.payload;
    },
    setWorkspaceModule: (state, action: PayloadAction<ErpModule>) => {
      state.workspaceModule = action.payload;
      persistWorkspaceModule(action.payload);
      markWorkspaceModuleUsed(action.payload);
      const owner = isOrgOwner(state.user?.permissions ?? [], state.user?.roleCode);
      if (!owner) {
        state.entryModule = action.payload;
        persistEntryModule(action.payload);
      }
    },
    setActiveModule: (state, action: PayloadAction<ErpModule>) => {
      state.workspaceModule = action.payload;
      persistWorkspaceModule(action.payload);
      const owner = isOrgOwner(state.user?.permissions ?? [], state.user?.roleCode);
      if (!owner) {
        state.entryModule = action.payload;
        persistEntryModule(action.payload);
      }
    },
    setMfaEnabled: (state, action: PayloadAction<boolean>) => {
      state.mfaEnabled = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(bootstrapSession.fulfilled, (state, action) => {
        const data = sessionToLoginResponse(action.payload);
        const hadAuth = state.isAuthenticated && state.user != null;
        const previousWorkspace = state.workspaceModule;
        const previousEntry = state.entryModule;

        applyLoginPayload(state, data, { restore: !hadAuth });

        if (hadAuth) {
          if (previousWorkspace && isEnabledWorkspaceModule(previousWorkspace, state.enabledModules)) {
            state.workspaceModule = previousWorkspace;
            persistWorkspaceModule(previousWorkspace);
          }
          if (previousEntry != null) {
            state.entryModule = previousEntry;
            persistEntryModule(previousEntry);
          }
        }
      })
      .addCase(refreshSessionInit.fulfilled, (state, action) => {
        const data = sessionToLoginResponse(action.payload);
        applySessionModuleLists(state, data);
        patchUserFromSessionRefresh(state, data);
      })
      .addCase(bootstrapSession.rejected, (state) => {
        // Ignore stale bootstrap failures when login/SSO already established a session.
        if (state.isAuthenticated && state.user != null) {
          return;
        }
        state.user = null;
        state.isAuthenticated = false;
        state.enabledFeatures = [];
        state.enabledModules = [];
        state.accessibleModules = [];
        state.entryModule = null;
        state.workspaceModule = null;
        state.mfaEnabled = false;
        state.forcePasswordChange = false;
        clearAuthStorage();
        clearEntryModuleStorage();
        clearLastAllowedPath();
      })
      .addCase(registerAccount.fulfilled, (state, action) => {
        applyLoginPayload(state, action.payload);
      })
      .addCase(addOrgApplication.fulfilled, (state, action) => {
        applyLoginPayload(state, action.payload);
      })
      .addCase(login.pending, (state) => {
        state.loading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        if (!action.payload.mfaRequired) {
          applyLoginPayload(state, action.payload);
        }
      })
      .addCase(verifyMfaLogin.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyMfaLogin.fulfilled, (state, action) => {
        state.loading = false;
        applyLoginPayload(state, action.payload);
      })
      .addCase(verifyMfaLogin.rejected, (state) => {
        state.loading = false;
      })
      .addCase(completeSsoLogin.fulfilled, (state, action) => {
        if (!action.payload.mfaRequired) {
          applyLoginPayload(state, action.payload);
        }
      })
      .addCase(switchWorkspace.fulfilled, (state, action) => {
        state.loading = false;
        applyLoginPayload(state, action.payload.data);
        state.workspaceModule = action.payload.module;
        persistWorkspaceModule(action.payload.module);
        markWorkspaceModuleUsed(action.payload.module);
      })
      .addCase(switchWorkspace.pending, (state) => {
        state.loading = true;
      })
      .addCase(switchWorkspace.rejected, (state) => {
        state.loading = false;
      })
      .addCase(login.rejected, (state) => {
        state.loading = false;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.enabledFeatures = [];
        state.enabledModules = [];
        state.accessibleModules = [];
        state.entryModule = null;
        state.workspaceModule = null;
        state.mfaEnabled = false;
        state.forcePasswordChange = false;
      });
  },
});

export const {
  setInitialized,
  hydrateFromLogin,
  clearAuth,
  setEnabledModules,
  setAccessibleModules,
  setWorkspaceModule,
  setActiveModule,
  setMfaEnabled,
} = authSlice.actions;
export default authSlice.reducer;
